import { ethers, Wallet } from "ethers";
import getContract, {getAutoRunContract, getBankContract, getAutoRun2Contract, getAutoRun3Contract} from "./contract";
import levelPrices, {autorunCost, autorun2Cost, autorun3Cost} from "./levelPrice";
import getTokenContract from "./tokenContract";
import getWallet from "./wallet";

export class TransactionService {
  constructor(userId, userAddress) {
    this.userId = userId;
    this.userAddress = userAddress;
  }

  lend = async (amount) => {
    const wallet = await getWallet();
    const tokenContract = await getTokenContract();

    const autoRunContract = await getAutoRunContract();
    const bankContract = await getBankContract();

    const isLenderRegistered = await autoRunContract.isUserRegisteredInAutoRun(this.userId);

    if (!isLenderRegistered) {
      throw new Error("You have to register in autorun to lend");
    }

    if(amount % 500 != 0 || amount == 0) {
      throw new Error("Enter amount like 500/1000/1500/2000");
    }

    const actualAmount = ethers.BigNumber.from(amount * (10**6));

    const userBalance = await tokenContract.balanceOf(this.userAddress);


    if(userBalance.lt(actualAmount)) {
      throw new Error("Insufficient balance");
    }

    const allowance = await tokenContract.allowance(this.userAddress, bankContract.address);

    if(allowance.lt(actualAmount)) {
      const txn = await tokenContract.approve(bankContract.address, actualAmount);
      await txn.wait();
    }

    const txn = await bankContract.lend(actualAmount);
    await txn.wait();

  }

  withdraw = async (amount) => {
    const wallet = await getWallet();

    const bankContract = await getBankContract();

    if(amount % 500 != 0 || amount == 0) {
      throw new Error("Enter amount like 500/1000/1500/2000");
    }

    const actualAmount = ethers.BigNumber.from(amount * (10**6));

    const txn = await bankContract.getLendingFundsBack(actualAmount);
    await txn.wait();

  }

  payDebt = async () => {
    const wallet = await getWallet();
    const tokenContract = await getTokenContract();

    const autoRunContract = await getAutoRunContract();
    const bankContract = await getBankContract();

    const lenderIdBN = await bankContract.borrowedFrom(this.userId);
    const lenderEarnedBN = await bankContract.lenderEarnedAmountFromBorrower(lenderIdBN, this.userId);

    const earnLimit = ethers.BigNumber.from(600 * (10**6));
    const debt = earnLimit.sub(lenderEarnedBN);
    
    const userBalance = await tokenContract.balanceOf(this.userAddress);

    if(userBalance.lt(debt)) {
      throw new Error("Insufficient balance");
    }

    const allowance = await tokenContract.allowance(this.userAddress, bankContract.address);

    if(allowance.lt(debt)) {
      const txn = await tokenContract.approve(bankContract.address, debt);
      await txn.wait();
    }
    
    const txn = await bankContract.payDebt();
    await txn.wait();

  }

  borrowAndRegister = async (lenderId) => {
    const wallet = await getWallet();
    const tokenContract = await getTokenContract();
    const matrixContract = await getContract();

    const autoRunContract = await getAutoRunContract();
    const bankContract = await getBankContract();

    const isBorrowed = await bankContract.isBorrowed(this.userId);

    const lendAmountBN = await bankContract.lenderIdToLendAmount(lenderId);

    const lendAmount = parseFloat(ethers.utils.formatUnits(lendAmountBN, "mwei"));

    if(lendAmount < 500 && !isBorrowed) {
      throw new Error("Lender has not enough money");
    }

    const isLenderRegistered = await autoRunContract.isUserRegisteredInAutoRun(lenderId);

    if (!isLenderRegistered) {
      throw new Error("Lender not registered");
    }

    const userInfoResponse = await matrixContract.userInfos(this.userId);
    const levelBought = ethers.BigNumber.from(userInfoResponse["levelBought"]).toNumber();

    if(levelBought < 4) {
      throw new Error("You must be Rank 4 to register in autorun");
    }

    const isBorrowerRegistered = await autoRunContract.isUserRegisteredInAutoRun(this.userId);

    if (isBorrowerRegistered) {
      throw new Error("You're already registered");
    }
    
    const actualAmount = ethers.BigNumber.from(125 * (10**6));
    
    const userBalance = await tokenContract.balanceOf(this.userAddress);
    
    if(userBalance.lt(actualAmount) && !isBorrowed) {
      throw new Error("Insufficient balance");
    }
    
    // const isInDebt = await bankContract.isBorrowedAlready(this.userId);

    const allowance = await tokenContract.allowance(this.userAddress, bankContract.address);
    
    if(allowance.lt(actualAmount) && !isBorrowed) {
      const txn = await tokenContract.approve(bankContract.address, actualAmount);
      await txn.wait();
    }

    if(!isBorrowed) {
      const txn = await bankContract.borrow(lenderId);
      await txn.wait();
    }

    const txn = await autoRunContract.joinAutorunwWithBorrowedFunds();
    await txn.wait();

  }

  buyLevel = async (level) => {
    try {
      const wallet = await getWallet();

      const tokenContract = await getTokenContract();
      const contract = await getContract();

      const userInfoResponse = await contract.userInfos(this.userId);
      const userLastLevel = ethers.BigNumber.from(userInfoResponse["levelBought"]).toNumber();
      if (userLastLevel+1 < level) {
        throw new Error("You have to buy previous slots first");
      }
      const userBalance = await tokenContract.balanceOf(this.userAddress);
      
      const levelPrice = ethers.BigNumber.from(levelPrices[level-1] * (10**6));
      console.log("levelPrice: ", levelPrice);
      
      if (userBalance.lt(levelPrice)) {
        throw new Error("You have Insufficient USDT balance");
      }
      
      const allowance = await tokenContract.allowance(this.userAddress, contract.address);
      
      if(allowance.lt(levelPrice)) {
        console.log("userLastLevel: ", userLastLevel);
        const tokenAmount = levelPrice;
        const txn = await tokenContract.approve(contract.address, tokenAmount);
        // const txn = await tokenContract.approve(contract.address, tokenAmount, {gasPrice: ethers.utils.parseUnits('100', 'gwei')});
        await txn.wait();
      }
      
      const transaction = await contract.uplevel(level);
      // const transaction = await contract.upgradeLevel(level, {gasPrice: ethers.utils.parseUnits(wallet.maxFeePerGas.toString(), 'gwei')});
      await transaction.wait();
      return true;
    } catch (error) {
      console.log(error);
      throw error;
    }
  }

  register = async (refId) => {
    try {
      const wallet = await getWallet();
      const tokenContract = await getTokenContract();
      const contract = await getContract();

      const refAddress = await contract.getAddressById(refId);
      const refInfoResponse = await contract.userInfos(refId);
      const isValidRef = ethers.BigNumber.from(refInfoResponse["id"]).toNumber() > 0;

      if(!isValidRef) {
        throw new Error("Invalid Upline Id. There is no referrer with this id match");
      }

      const id = await contract.getIdByAddress(this.userAddress);
      const isAlreadyRegistered = ethers.BigNumber.from(id).toNumber() > 0;

      if(isAlreadyRegistered) {
        throw new Error("User is already registered");
      }

      const userBalance = await tokenContract.balanceOf(this.userAddress)

      const levelPrice = ethers.BigNumber.from(levelPrices[0] * (10**6));
      if (userBalance.lt(levelPrice)) {
        throw new Error("You have Insufficient balance");
      }
      const allowance = await tokenContract.allowance(this.userAddress, contract.address);

      if(allowance.lt(levelPrice)) {
        const tokenAmount = levelPrice;
        const txn = await tokenContract.approve(contract.address, tokenAmount);
        // const txn = await tokenContract.approve(contract.address, tokenAmount, {gasPrice: ethers.utils.parseUnits('100', 'gwei')});
        await txn.wait();
      }
      const transaction = await contract.register(refId);
      // const transaction = await contract.register(refId, {gasPrice: ethers.utils.parseUnits(wallet.maxFeePerGas.toString(), 'gwei')});
      await transaction.wait();
      const userId = ethers.BigNumber.from(await contract.getIdByAddress(this.userAddress)).toNumber();
      return userId;
    } catch (error) {
      throw error;
    }
  }

  joinAutorun = async () => {
    
    try {
      const wallet = await getWallet();

      const tokenContract = await getTokenContract();
      const contract = await getContract();
      const autoRunContract = await getAutoRunContract();

      const userInfoResponse = await contract.userInfos(this.userId);
      const userLastLevel = ethers.BigNumber.from(userInfoResponse["levelBought"]).toNumber();

      if (userLastLevel < 4) {
        throw new Error("You are not qualify on Rank 4");
      }

      const userBalance = await tokenContract.balanceOf(this.userAddress);
      
      const cost = ethers.BigNumber.from(autorunCost * (10**6));
      
      if (userBalance.lt(cost)) {
        throw new Error("You have Insufficient USDT");
      }
      
      const allowance = await tokenContract.allowance(this.userAddress, autoRunContract.address);
      
      if(allowance.lt(cost)) {
        const tokenAmount = cost;
        const txn = await tokenContract.approve(autoRunContract.address, tokenAmount);
        // const txn = await tokenContract.approve(contract.address, tokenAmount, {gasPrice: ethers.utils.parseUnits('100', 'gwei')});
        await txn.wait();
      }
      
      const transaction = await autoRunContract.joinAutoRun();
      await transaction.wait();
      return true;
    } catch (error) {
      console.log(error);
      throw error;
    }
  }

  joinAutorun2 = async () => {
    
    try {
      const wallet = await getWallet();

      const tokenContract = await getTokenContract();
      const contract = await getContract();
      const autoRunContract = await getAutoRun2Contract();

      const userInfoResponse = await contract.userInfos(this.userId);
      const userLastLevel = ethers.BigNumber.from(userInfoResponse["levelBought"]).toNumber();

      if (userLastLevel < 4) {
        throw new Error("You are not qualify on Rank 4");
      }

      const userBalance = await tokenContract.balanceOf(this.userAddress);
      
      const cost = ethers.BigNumber.from(autorun2Cost * (10**6));
      
      if (userBalance.lt(cost)) {
        throw new Error("You have Insufficient USDT");
      }
      
      const allowance = await tokenContract.allowance(this.userAddress, autoRunContract.address);
      
      if(allowance.lt(cost)) {
        const tokenAmount = cost;
        const txn = await tokenContract.approve(autoRunContract.address, tokenAmount);
        // const txn = await tokenContract.approve(contract.address, tokenAmount, {gasPrice: ethers.utils.parseUnits('100', 'gwei')});
        await txn.wait();
      }
      
      const transaction = await autoRunContract.joinAutoRun();
      await transaction.wait();
      return true;
    } catch (error) {
      console.log(error);
      throw error;
    }
  }

  joinAutorun3 = async () => {
    
    try {
      const wallet = await getWallet();

      const tokenContract = await getTokenContract();
      const contract = await getContract();
      const autoRunContract = await getAutoRun3Contract();

      const userInfoResponse = await contract.userInfos(this.userId);
      const userLastLevel = ethers.BigNumber.from(userInfoResponse["levelBought"]).toNumber();

      if (userLastLevel < 4) {
        throw new Error("You are not qualify on Rank 5");
      }

      const userBalance = await tokenContract.balanceOf(this.userAddress);
      
      const cost = ethers.BigNumber.from(autorun3Cost * (10**6));
      
      if (userBalance.lt(cost)) {
        throw new Error("You have Insufficient USDT");
      }
      
      const allowance = await tokenContract.allowance(this.userAddress, autoRunContract.address);
      
      if(allowance.lt(cost)) {
        const tokenAmount = cost;
        const txn = await tokenContract.approve(autoRunContract.address, tokenAmount);
        // const txn = await tokenContract.approve(contract.address, tokenAmount, {gasPrice: ethers.utils.parseUnits('100', 'gwei')});
        await txn.wait();
      }
      
      const transaction = await autoRunContract.joinAutoRun();
      await transaction.wait();
      return true;
    } catch (error) {
      console.log(error);
      throw error;
    }
  }

}